'use server';

import createClient from "openapi-fetch";
import type { paths } from "@/app/api/schema";
import { MIN_PASSWORD_LENGTH } from "@/lib/password-reset";
import type { LoginState } from "@/types/auth";

const SERVICE_URL = process.env.AUTOMATARI_SERVICE_URL;
const TIMEOUT_MS = 15_000;
const INVALID_LINK = "El enlace para restablecer la contraseña no es válido o ya expiró. Solicita uno nuevo.";

function client() {
  return SERVICE_URL ? createClient<paths>({ baseUrl: SERVICE_URL }) : null;
}

function detailOf(error: unknown): string | null {
  const detail = (error as { detail?: unknown } | undefined)?.detail;
  return typeof detail === "string" ? detail : null;
}

export async function requestPasswordResetAction(_: LoginState, formData: FormData): Promise<LoginState> {
  const email = String(formData.get("email") ?? "").trim();
  if (!email) return { ok: false, message: "Ingresa tu correo electrónico." };

  const api = client();
  if (!api) return { ok: false, message: "El servicio no está disponible en este momento." };

  try {
    const { data, error } = await api.POST("/api/auth/password/forgot", {
      body: { email },
      signal: AbortSignal.timeout(TIMEOUT_MS),
    });
    if (error || !data) return { ok: false, message: "Revisa que el correo esté bien escrito." };
    return { ok: true, message: data.detail };
  } catch {
    return { ok: false, message: "Error de conexión con el servidor." };
  }
}

/** Server-side check before rendering the new password form; null means the link is usable. */
export async function verifyPasswordResetToken(token: string): Promise<string | null> {
  if (!token) return INVALID_LINK;
  const api = client();
  if (!api) return "El servicio no está disponible en este momento.";
  try {
    const { error } = await api.POST("/api/auth/password/verify", {
      body: { token },
      signal: AbortSignal.timeout(TIMEOUT_MS),
    });
    return error ? detailOf(error) ?? INVALID_LINK : null;
  } catch {
    return "Error de conexión con el servidor.";
  }
}

export async function resetPasswordAction(_: LoginState, formData: FormData): Promise<LoginState> {
  const token = String(formData.get("token") ?? "");
  const password = String(formData.get("password") ?? "");
  const confirm = String(formData.get("confirm") ?? "");

  if (password.length < MIN_PASSWORD_LENGTH) {
    return { ok: false, message: `La contraseña debe tener al menos ${MIN_PASSWORD_LENGTH} caracteres.` };
  }
  if (password !== confirm) return { ok: false, message: "Las contraseñas no coinciden." };

  const api = client();
  if (!api) return { ok: false, message: "El servicio no está disponible en este momento." };

  try {
    const { data, error } = await api.POST("/api/auth/password/reset", {
      body: { token, new_password: password },
      signal: AbortSignal.timeout(TIMEOUT_MS),
    });
    if (error || !data) return { ok: false, message: detailOf(error) ?? "No se pudo actualizar la contraseña." };
    return { ok: true, message: data.detail };
  } catch {
    return { ok: false, message: "Error de conexión con el servidor." };
  }
}
